import { DOMSerializer } from 'prosemirror-model'
import { kebabCase } from 'lodash-es'
import * as marked from 'marked'
// Relative rather than `~/lib`: node --test imports this file and has no alias.
import { findFontOption } from '../../javascript/lib/font_options.js'

// Where interactive Tippy popups anchored inside the editor (BubbleMenu, the "{" / "/" / "@"
// suggestion menus) should mount. A `rotate` transform on an ancestor builder node creates a
// new containing block, so an interactive Tippy popup left to its default appendTo (a DOM
// sibling of its reference element, inside that transformed subtree) visibly rotates along
// with the content instead of staying upright. The shared `.tippy-portal` div is rendered at
// the very end of <body> (see layouts_helper.rb) specifically to escape that — already the
// established pattern for interactive Tippy/dropdown popups elsewhere in the app. Falls back
// to <body> for a layout that doesn't render the portal.
export function resolveTippyPortal() {
  return document.querySelector('.tippy-portal') || document.body
}

// Tippy's setProps walks nested [data-tippy-root] descendants and calls
// popperInstance.forceUpdate. Nested instances with detachOnUnmount: false
// stay in the DOM after hide with popperInstance === null (toolbar color
// pickers, font dropdowns, button tooltips). That TypeError aborts the
// parent BubbleMenu update, so TipTap toolbar commands appear to no-op.
// Swallow only that specific TypeError — anything else rethrows.
export function guardTippySetProps(instance) {
  if (!instance || typeof instance.setProps !== 'function') return instance

  const originalSetProps = instance.setProps.bind(instance)
  instance.setProps = (partialProps) => {
    try {
      return originalSetProps(partialProps)
    } catch (error) {
      if (
        error instanceof TypeError &&
        /forceUpdate/.test(error.message || '')
      ) {
        return
      }
      throw error
    }
  }
  return instance
}

// Dragging a highlight upwards across the floating BubbleMenu toolbar collapses the selection
// to the toolbar's position and jumps the scroll back down. Driving the real editor in
// Chromium, a bottom-to-top drag that crosses the *visible* toolbar keeps 129 of the 839
// characters it swept; taking the toolbar out of hit-testing for the drag keeps all 839.
//
// The toolbar only has to be visible to do this, which is why the bug feels intermittent:
// BubbleMenu's updateDelay means a brisk uninterrupted drag never re-shows it, and only a
// pause part-way through — or a toolbar left over from a previous selection — puts it back
// under the pointer.
//
// The builder floats more overlays over the same text: the proximity-adder and level-selector
// pills, which are pointer-active, `select-none`, and repositioned from a document-level
// mousemove. The toolbar is the one reproduced above; the pills fit the same profile, so the
// guard covers them too rather than waiting for the same bug to be reported against them.
//
// guardSelectionDrags marks the document for as long as a text-selection drag is in progress.
// A CSS rule (see editor_component.scss) takes those overlays out of hit-testing for that
// duration; they are fully interactive again the moment the pointer is released.
//
// Two things this must NOT go back to being:
//
// Installed from the toolbar's own lifecycle. BubbleMenu builds its tippy lazily, on the first
// selection that shows the toolbar, so arming from that tippy's onCreate left the user's very
// first drag after page load unguarded — the attempt they actually make first. It has to arm
// from the editor, before any toolbar exists.
//
// An inline `pointer-events` style. Tippy rewrites `popper.style.pointerEvents` in its own
// handleStyles() on every show and every setProps, and BubbleMenu calls setProps on each
// editor update, so a style set from a tippy hook is overwritten within the same show.
export const SELECTION_DRAG_CLASS = 'tiptap-drag-selecting'

// The toolbar's own tippy root, published as a class by the tippy that builds it — the same idiom,
// on the same `classList.add` line, as the `tw` beside it and as `popover-controller-tippy` in
// popover_controller.js. This is the canonical explanation; the stylesheets that use the class, and
// the test that guards it, point here rather than repeating it.
//
// The drag guard below used to select it as `[data-tippy-root]:has(.tiptap-toolbar)`, because a
// tippy root carries nothing else to tell the toolbar's apart from every other popup's. It wants
// the root AND its descendants, and **a `:has()` followed by a descendant `*` is the shape that
// makes a `:has()` ruinous**: the rightmost compound is universal, so EVERY element in the document
// is a candidate subject and each one re-evaluates the `:has()` up its ancestor chain on every
// style invalidation — paid whether or not anything matches. Measurements in
// doc/plans/2026-09-02-page_builder_hover_latency.md §10.
//
// The class is exactly equivalent: the toolbar is moved INTO this popper, so it is the only tippy
// root that can contain `.tiptap-toolbar`, and before `onCreate` runs there is no root at all. The
// rewrite does drop one specificity step (0,3,0 → 0,2,0) — checked: nothing else in `app/**`
// targets a tippy root, and this rule carries `!important`.
export const TOOLBAR_TIPPY_CLASS = 'tiptap-toolbar-tippy'

const documentsWithSelectionDragGuard = new WeakSet()

// One listener set per document, however many editors the page mounts. Nothing to tear down
// per editor: the drag state lives on <html>, not on any component, so a toolbar that is
// created, shown, hidden or destroyed mid-drag cannot desynchronise it.
export function guardSelectionDrags(doc) {
  if (!doc?.documentElement) return
  if (documentsWithSelectionDragGuard.has(doc)) return
  documentsWithSelectionDragGuard.add(doc)

  const root = doc.documentElement
  const win = doc.defaultView

  const stopSelecting = () => root.classList.remove(SELECTION_DRAG_CLASS)

  const startSelecting = (event) => {
    // Engage only for a press that lands in editable text, because only that can start a
    // selection drag. Everything else — a toolbar button, a builder pill, a node handle — is
    // that control's own interaction, and pulling the overlays out from under it mid-press
    // would break the very controls this guard exists to keep usable.
    const target = event?.target
    if (typeof target?.closest !== 'function') return
    if (!target.closest('[contenteditable="true"]')) return
    // A popup can host its own editable surface; that is the popup's interaction, not a drag
    // across the page's text.
    if (target.closest('[data-tippy-root]')) return
    root.classList.add(SELECTION_DRAG_CLASS)
  }

  // Self-heal. A release these listeners can miss — a native drag, a context menu, a
  // devtools pause — would otherwise leave the toolbar permanently unclickable. The first
  // pointer movement with no button held clears it.
  const stopSelectingIfReleased = (event) => {
    if (event.buttons === 0 && root.classList.contains(SELECTION_DRAG_CLASS)) {
      stopSelecting()
    }
  }

  doc.addEventListener('mousedown', startSelecting, { capture: true })
  doc.addEventListener('mouseup', stopSelecting, { capture: true })
  doc.addEventListener('mousemove', stopSelectingIfReleased, { capture: true })

  if (!win) return

  win.addEventListener('pointerup', stopSelecting, { capture: true })
  win.addEventListener('pointercancel', stopSelecting, { capture: true })
  win.addEventListener('blur', stopSelecting)

  // The builder renders the page being edited in a preview iframe, so a drag started in the
  // editor can be released over the surrounding admin chrome, where none of the above fire.
  try {
    const parentWin = win.parent
    if (parentWin && parentWin !== win) {
      parentWin.addEventListener('mouseup', stopSelecting, { capture: true })
      parentWin.addEventListener('pointerup', stopSelecting, { capture: true })

      // These are the one pair that outlives their own document: the builder re-navigates
      // the preview iframe on save, and each new document installs its own guard. Drop them
      // with the document that owns them, or the admin window accumulates a pair per
      // navigation, each retaining a detached <html>.
      //
      // Only when the document is really going away, though. A persisted pagehide means
      // bfcache, and that document can come back — with no second install to re-add these,
      // since it is already in documentsWithSelectionDragGuard. So stay bound, and tear down
      // on the pagehide that finally discards it.
      win.addEventListener('pagehide', (event) => {
        if (event?.persisted) return
        parentWin.removeEventListener('mouseup', stopSelecting, {
          capture: true,
        })
        parentWin.removeEventListener('pointerup', stopSelecting, {
          capture: true,
        })
      })
    }
  } catch (_) {
    // Cross-origin parent — nothing to bind.
  }
}

// BubbleMenu portals the toolbar into `.tippy-portal` so it stays upright on
// rotated builder nodes. Stimulus then refuses to invoke `->tiptap--editor#…`
// actions: willBeInvokedByEvent requires
// `event.target.closest('[data-controller~=tiptap--editor]')` to be the
// editor element, and the portaled toolbar is no longer inside it. Every
// toolbar click (bold, color, align, link, …) silently no-ops.
// The editor controller re-invokes those actions by listening on toolbarEl.

export function matchingTiptapEditorMethod(actionAttr, eventType) {
  if (!actionAttr || !eventType) return null

  for (const part of actionAttr.split(/\s+/)) {
    const match = part.match(/^(.+?)(?:@\w+)?->tiptap--editor#(\w+)$/)
    if (match && match[1] === eventType) return match[2]
  }
  return null
}

export function stimulusParamsFromElement(element, identifier) {
  const params = {}
  if (!element?.attributes || !identifier) return params

  const pattern = new RegExp(`^data-${identifier}-(.+)-param$`, 'i')
  for (const { name, value } of Array.from(element.attributes)) {
    const match = name.match(pattern)
    const key = match && match[1]
    if (!key) continue
    params[key.replace(/-([a-z])/g, (_, c) => c.toUpperCase())] = value
  }
  return params
}

export function collectTiptapEditorEventTypes(root) {
  const events = new Set()
  if (!root) return events

  const nodes = [
    root,
    ...Array.from(root.querySelectorAll?.('[data-action]') || []),
  ]
  for (const el of nodes) {
    const attr = el.getAttribute?.('data-action')
    if (!attr) continue
    for (const part of attr.split(/\s+/)) {
      const match = part.match(/^(.+?)(?:@\w+)?->tiptap--editor#/)
      if (match) events.add(match[1])
    }
  }
  return events
}

const toolbarBtnAttributes = {
  alignLeft: { textAlign: 'left' },
  alignRight: { textAlign: 'right' },
  alignCenter: { textAlign: 'center' },
  alignJustify: { textAlign: 'justify' },
}

export function updateToolbarButtonsActiveState(editor, editorController) {
  // Scope to this editor's own toolbar. A document-wide query walks every toolbar on
  // the page -- on a large builder document that is hundreds of them, once per editor
  // per transaction. Fall back to the document when the controller has no toolbar
  // reference yet, so behavior is unchanged for callers that pass no controller.
  const root = editorController?.toolbarEl ?? document
  const buttons = root.querySelectorAll('.tiptap-toolbar-btn')
  const activeClass = 'is-active'
  buttons.forEach(function (button) {
    let name =
      button.getAttribute('data-tiptap--editor-action-param') ||
      button.getAttribute('data-tiptap-mark')
    if (!name) return

    // Handle undo/redo with disabled state
    if (name === 'undo') {
      const canUndo = editor.can().undo()
      button.disabled = !canUndo
      return
    } else if (name === 'redo') {
      const canRedo = editor.can().redo()
      button.disabled = !canRedo
      return
    }

    // For other buttons, check if the mark/node is active
    if (toolbarBtnAttributes[name]) name = toolbarBtnAttributes[name]
    const isActive = editor.isActive(name)

    if (button.classList.contains(activeClass)) {
      if (!isActive) {
        button.classList.remove(activeClass)
      }
    } else if (isActive) {
      button.classList.add(activeClass)
    }
  })

  // Update font family and font size inputs
  if (editorController) {
    updateFontInputs(editor, editorController)
  }
}

function updateFontInputs(editor, editorController) {
  // Get current font properties from selected text
  const textStyleAttrs = editor.getAttributes('textStyle')
  let fontFamily = textStyleAttrs.fontFamily
  let fontSize = textStyleAttrs.fontSize
  const fontFamilyContainer = editorController.toolbarControl(
    'fontFamilyContainer'
  )
  const wantsComputedFontFamily = !fontFamily && !!fontFamilyContainer
  // Both computed fallbacks below read the same computed style; get it once.
  const caretStyle =
    wantsComputedFontFamily || !fontSize ? computedStyleAtCaret(editor) : null

  // Fallback when selection has mixed/no fonts
  // (getAttributes returns undefined for mixed formatting).
  // Prefer the font the text actually renders in, as for font-size below: a
  // style-guide variant (sm/md/lg/xl) takes its font from CSS variables, and
  // fontFamilyValue is then the custom-typography default ('Inter') that
  // nobody picked (ENG-26884).
  if (wantsComputedFontFamily) {
    fontFamily = fetchDefaultFontFamily(editorController, caretStyle)
  }
  if (!fontFamily && editorController.hasFontFamilyValue) {
    fontFamily = editorController.fontFamilyValue
  }
  // Prefer computed font-size when there is no inline textStyle mark.
  // Style-guide variants (sm/md/lg/xl) size text via CSS variables on the
  // builder node, not TipTap marks — without this the toolbar falls back to
  // the custom-typography default (14px) and looks wrong next to a 49px XL
  // headline. Mirror fetchDefaultColor for text color.
  // Round only the computed path (subpixel values like "48.999px"); keep
  // explicit inline marks such as "10.5px" exact so re-apply does not drift.
  if (!fontSize) {
    fontSize =
      fetchDefaultFontSize(editorController, caretStyle) ||
      fallbackConfiguredFontSize(editorController)
  }

  // Sync font family to toolbar input
  if (fontFamilyContainer && fontFamily) {
    const fontFamilyInput = fontFamilyContainer.querySelector(
      'input[type="hidden"]'
    )
    // Only on a change: this runs on every transaction (each keystroke), and the picker
    // re-filters its whole catalog on each update it receives.
    if (isSyncable(fontFamilyInput) && fontFamilyInput.value !== fontFamily) {
      fontFamilyInput.value = fontFamily
      fontFamilyInput.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  // Sync font size and unit to toolbar inputs
  if (fontSize) {
    const fontSizeStr = fontSize.toString()
    // Extract numeric value and unit from "16px" → ["16", "px"]
    const match = fontSizeStr.match(/^([\d.]+)\s*([a-z%]*)$/i)
    if (match) {
      const sizeValue = match[1]
      const unit = match[2] || 'px'

      const fontSizeInput = editorController.toolbarControl('fontSizeInput')
      const fontSizeUnitSelect =
        editorController.toolbarControl('fontSizeUnitSelect')

      if (isSyncable(fontSizeInput)) fontSizeInput.value = sizeValue
      if (isSyncable(fontSizeUnitSelect)) fontSizeUnitSelect.value = unit
    }
  }
}

// A toolbar input the sync may write: it's there, and the user isn't typing in it.
function isSyncable(input) {
  return !!input && document.activeElement !== input
}

// Configured Stimulus defaults (custom typography track). Used only when the
// selection has no DOM to sample — usually wrong for style-guide variants.
function fallbackConfiguredFontSize(editorController) {
  if (!editorController.hasFontSizeValue) return null

  let fontSize = editorController.fontSizeValue
  const unit = editorController.hasFontSizeUnitValue
    ? editorController.fontSizeUnitValue
    : 'px'
  if (fontSize && !fontSize.toString().includes(unit)) {
    fontSize = fontSize + unit
  }
  return fontSize
}

// The element whose computed style is what the text at the caret renders with.
function elementAtCaret(editor) {
  if (!editor?.view) return null

  let element = null
  try {
    const { from } = editor.state.selection
    const domAtPos = editor.view.domAtPos(from)
    element = domAtPos.node
    if (element?.nodeType === Node.TEXT_NODE) {
      element = element.parentElement
    }
  } catch (_e) {
    element = null
  }

  // Empty doc / odd selection: walk the editor DOM or the focused block
  if (!element || element === editor.view.dom) {
    element =
      editor.view.dom.querySelector('.has-focus') ||
      editor.view.dom.querySelector('p, h1, h2, h3, h4, h5, h6') ||
      editor.view.dom
  }

  return element
}

// The computed style the text at the caret renders with. Reading it forces a
// style recalc, so one toolbar sync reads it once (see toolbarNeedsSync).
function computedStyleAtCaret(editor) {
  const element = elementAtCaret(editor)
  if (!element || typeof window === 'undefined' || !window.getComputedStyle) {
    return null
  }

  return window.getComputedStyle(element)
}

// Computed font-size at the caret / selection (px). Covers style-guide CSS vars
// and any parent-driven size that TipTap has no mark for.
export function fetchDefaultFontSize(
  editorController,
  style = computedStyleAtCaret(editorController?.editor)
) {
  if (!style) return null

  const size = style.fontSize
  // getComputedStyle always returns px (often subpixel, e.g. "48.999px")
  if (!size || size === '0px') return null
  const match = size.match(/^([\d.]+)px$/i)
  if (!match) return size
  return `${Math.round(parseFloat(match[1]))}px`
}

// The font picker's value for the font the text at the caret renders in (for a
// mixed selection, the font at its start). Null when the picker has no option
// for it — a system font, or `monospace` on code — so the caller keeps its own
// fallback (see findFontOption for why).
export function fetchDefaultFontFamily(
  editorController,
  style = computedStyleAtCaret(editorController?.editor)
) {
  const options =
    typeof window === 'undefined'
      ? null
      : window.FONT_FAMILY_OPTIONS_FOR_FONT_SELECT_COMPONENT
  if (!style || !options) return null

  // The first family of the stack; a quoted one may itself contain a comma.
  const stack = style.fontFamily || ''
  const family = unquoteFontFamily(
    stack.match(/^\s*("[^"]*"|'[^']*'|[^,]*)/)[1].trim()
  )
  if (!family) return null

  const option = findFontOption(options, family)
  return option ? option.value : null
}

export function fireSetDecorationFromEditorEvent(editorController) {
  const config = editorController.editor.getAttributes('decoration').config
  const decorationDropdown =
    editorController.toolbarControl('decorationDropdown')

  if (config && decorationDropdown)
    editorController.dispatch('setDecoration', {
      target: decorationDropdown,
      detail: { config },
    })
}

export function fireSetHighlightColorEvent(editorController) {
  let color = editorController.editor.getAttributes('highlight').color
  if (!color) {
    color = fetchDefaultBgColor(editorController)
  }

  const highlightColor = editorController.toolbarControl('highlightColor')
  if (highlightColor)
    dispatchSetColorEvent(editorController, highlightColor, color)
}

export function fireSetTextColorEvent(editorController) {
  let color = editorController.editor.getAttributes('textStyle').color
  if (!color) {
    color = fetchDefaultTextColor(editorController)
  }

  const textColor = editorController.toolbarControl('textColor')
  if (textColor)
    dispatchSetColorEvent(editorController, textColor, color || '#000000')
}

export function dispatchSetColorEvent(controller, target, color) {
  controller.dispatch('color-swatch-picker:selectColorOnPalette', {
    target: target,
    prefix: false,
    detail: { color },
  })
}

function fetchDefaultColor(editorController, property) {
  let element = editorController.editor.view.dom.querySelector('.has-focus')
  const textContent = getTextContentBetween(editorController.editor)

  if (element && textContent) {
    element.childNodes.forEach((node) => {
      // The text nodes will not have tagName, for them we need the parent element only
      // The text color is applied inside a span tag so we don't want to match them
      if (
        node.tagName &&
        node.tagName !== 'SPAN' &&
        node.textContent === textContent.trim()
      ) {
        element = node
      }
    })
  }

  if (element) {
    return window.getComputedStyle(element, null).getPropertyValue(property)
  }
}

function fetchDefaultBgColor(editorController) {
  return fetchDefaultColor(editorController, 'background-color')
}

function fetchDefaultTextColor(editorController) {
  return fetchDefaultColor(editorController, 'color')
}

export function getHTMLContentBetween(editor) {
  const { view, state } = editor
  const { from, to } = view.state.selection
  const nodesArray = []

  state.doc.nodesBetween(from, to, (node, pos, parent) => {
    if (parent === state.doc) {
      const serializer = DOMSerializer.fromSchema(editor.schema)
      const dom = serializer.serializeNode(node)
      const tempDiv = document.createElement('div')
      tempDiv.appendChild(dom)
      nodesArray.push(tempDiv.innerHTML)
    }
  })

  return nodesArray.join('')
}

export function getTextContentBetween(editor) {
  const { view, state } = editor
  const { from, to } = view.state.selection
  return state.doc.textBetween(from, to, '')
}

// Create the DOM element because it is not necessary that the selection is always a link - it could be a link inside li
// in that case the selection returns the complete ul element so create an anchor tag element based on the selection
export const createAnchorElementFromSelection = (editor) => {
  const { state } = editor

  const { ranges } = state.selection

  let selectionRange = 0
  const markRanges = []

  ranges.forEach(({ $from, $to }) => {
    const from = $from.pos
    const to = $to.pos

    state.doc.nodesBetween(from, to, (node, pos) => {
      if (!node.isText && !node.marks.length) {
        return
      }

      const relativeFrom = Math.max(from, pos)
      const relativeTo = Math.min(to, pos + node.nodeSize)
      const range = relativeTo - relativeFrom

      selectionRange += range

      markRanges.push(
        ...node.marks.map((mark) => ({
          mark,
          from: relativeFrom,
          to: relativeTo,
        }))
      )
    })
  })

  if (selectionRange === 0) {
    return false
  }

  const matchedRange = markRanges.filter((markRange) => {
    return 'link' === markRange.mark.type.name
  })

  const mark = matchedRange[0]?.mark

  if (mark) {
    const a = document.createElement('a')

    for (const key in mark.attrs) {
      if (mark.attrs[key]) {
        a.setAttribute(kebabCase(key), mark.attrs[key])
      }
    }

    return a
  }
}

export function getExtensionOption(editor, extensionName, optionName) {
  const extension = getExtension(editor, extensionName)

  if (!extension) throw new Error(`Extension ${extensionName} not found`)

  return extension.options[optionName]
}

export function getExtension(editor, extensionName) {
  const extension = editor.extensionManager.extensions.find(
    (extension) => extension.name === extensionName
  )

  return extension
}

export function getBuilderResourcesForLinkPicker() {
  const resources = []
  const elements = window.top.document.querySelectorAll(
    '.js-builder-editor-sidebar .js-node-tree-element-node'
  )
  elements.forEach((element) => {
    const dataset = element.dataset
    const id = dataset.nodeId
    resources.push({
      id: id,
      label: dataset.nodeName,
      internalUri: `#node_${id}`,
      __typename: 'AnchorTargetLink',
    })
  })
  return resources
}

// Convert a Markdown string to sanitized HTML for insertContent: strip embedded
// raw HTML and disallow non-http(s)/mailto/relative link & image hrefs.
// Trailing whitespace is trimmed — marked ends its output with a newline, which
// ProseMirror would otherwise parse into a stray empty paragraph at the end.
export function markdownToHtml(markdown) {
  const html = marked.parse(markdown, {
    walkTokens(token) {
      if (token.type === 'html' || token.type === 'tag') {
        token.text = ''
        token.raw = ''
      }
      if (token.type === 'link' || token.type === 'image') {
        const href = token.href || ''
        const hasAllowedScheme = /^(https?:|mailto:)/i.test(href)
        const isRelative =
          href &&
          (href.startsWith('#') ||
            (href.startsWith('/') && !href.startsWith('//')) ||
            (!/^[a-z][a-z0-9+.-]*:/i.test(href) && !href.startsWith('//')))
        if (!hasAllowedScheme && !isRelative) token.href = ''
      }
    },
  })
  return html.replace(/> *\n *</g, '><').trim()
}

// Pasted text with nothing but a URL in it — no surrounding words. Shared by
// embedTargetFor (video-embed detection) and isBareUrlOverSelection below.
export function isBareUrl(text) {
  return /^https?:\/\/\S+$/.test(text)
}

// A bare URL pasted over a highlighted selection should hyperlink the selection
// (via @tiptap/extension-link's own paste handler, `linkOnPaste: true` in
// extensions.js) instead of replacing it with the raw URL as if it were Markdown.
export function isBareUrlOverSelection(pastedText, selectionEmpty) {
  return !selectionEmpty && isBareUrl(pastedText)
}

// What handlePaste does with a pasted URL. A selection always becomes a link, even
// for a video URL. An embeddable URL on a form with video attachments attaches as
// an embed — unless that URL is already attached (or still loading), in which case
// it goes into the text as a link instead of being refused. null = not our paste.
export function pastedUrlAction({
  text,
  selectionEmpty,
  embeddable,
  attached,
}) {
  if (isBareUrlOverSelection(text, selectionEmpty)) return 'link-selection'
  if (!embeddable) return null
  return attached ? 'insert-link' : 'embed'
}

// Render Markdown as rich content, but never let a parse failure drop the text —
// fall back to the raw string, which insertContent adds verbatim.
export function markdownToHtmlOrText(markdown) {
  try {
    return markdownToHtml(markdown)
  } catch (error) {
    console.error('Markdown conversion failed, inserting plain text:', error)
    return markdown
  }
}

// Shared by every `editor:setContent` listener.
//
// `markdown` marks `content` as a Markdown string that must be converted before it
// reaches ProseMirror. Without it a Markdown document is parsed as HTML: newlines
// collapse to spaces and `#`/`**` survive as literal characters, flattening the whole
// document to one line.
//
// `undoable` replaces the document in a normal transaction so Ctrl+Z restores the prior
// content (plain setContent resets history). The replaced range has to span the whole
// document: a selection-based replace keeps the block the selection started in, so the
// new content lands *inside* the first node — a leading <h1> swallows everything after it.
export function applySetContent(editor, detail) {
  const content = detail.markdown
    ? markdownToHtmlOrText(detail.content)
    : detail.content
  const chain = detail.undoable
    ? editor
        .chain()
        .insertContentAt(
          { from: 0, to: editor.state.doc.content.size },
          content
        )
    : editor.chain().setContent(content)
  if (detail.focus) chain.focus(detail.focus)
  chain.run()
}

const CSS_IDENTIFIER = /^-?[a-zA-Z_][\w-]*$/

// A family name survives unquoted only if every space-separated part is a CSS identifier,
// and an identifier can never start with a digit. `font-family: Source Sans 3` is therefore
// a parse error: the browser throws the whole declaration away and falls back to its
// default serif. Anything already quoted, a var() reference, or a whole stack with its own
// fallbacks is left exactly as it came in.
export function cssFontFamilyValue(fontFamily) {
  const value = (fontFamily || '').trim()
  if (!value || value.includes(',') || value.startsWith('var(')) return value
  if (/^(["']).*\1$/.test(value)) return value
  if (value.split(' ').every((part) => CSS_IDENTIFIER.test(part))) return value

  return `'${value.replace(/(['\\])/g, '\\$1')}'`
}

// '"Advent Pro"' -> 'Advent Pro'. See fontFamilyAttribute.parseHTML for why.
function unquoteFontFamily(fontFamily) {
  const quoted = fontFamily.match(/^(["'])(.*)\1$/)
  return quoted ? quoted[2] : fontFamily
}

// The editor's own font-family choice on a run of text. It renders to an inline style, so it
// outranks the style guide's `font-family` rule on the surrounding .wysiwyg-content and stays
// the winner — which only holds if the value is valid CSS, since an invalid one is discarded
// and the text silently drops back to the style guide font.
//
// Lives here rather than inline in extensions.js so the parse/render round trip can be
// exercised on its own.
export const fontFamilyAttribute = {
  default: null,

  // The browser reports a quoted family back with its quotes ('"Advent Pro"'), but the font
  // picker's options are unquoted, so the lookup misses unless we unwrap them. Only a matched
  // pair is stripped: a stack like `Garamond, "Hoefler Text"` must survive intact.
  parseHTML: (element) => {
    const fontFamily = element.style.fontFamily
    if (!fontFamily) return null

    return unquoteFontFamily(fontFamily)
  },

  renderHTML: (attributes) => {
    if (!attributes.fontFamily) return {}

    return { style: `font-family: ${cssFontFamilyValue(attributes.fontFamily)}` }
  },
}

// The stylesheet that actually makes a picked font usable, or null when there is nothing for us
// to load.
//
// The font picker only ever loads a *glyph subset* of each family for its dropdown previews --
// webfontloader's `text:` option, filled with the characters of the font names on screen -- so the
// @font-face it leaves behind carries a unicode-range covering those characters and nothing else.
// Applying that family to real text then renders every other character in the fallback face, which
// reads as "the font didn't load". The full stylesheet is only emitted server-side, by
// Builder::HeadContentComponent, on a whole-document render, so it used to take a page reload.
//
// The three sources are the ones Form::FontSelectComponent puts in the catalog, and
// HeadContentComponent#render_fonts dispatches on the same set server-side.
export function webfontStylesheetHref(option) {
  const source = option?.extraData?.source

  // A custom font's own stylesheet already declares every weight the account uploaded.
  if (source === 'custom') return option.extraData.url || null
  // A style-guide entry resolves to a CSS variable whose family the guide's stylesheet loads, and
  // anything else has no css2 answer to give.
  if (source !== 'google') return null

  const family = (option.value || '').trim()
  if (!family) return null

  // Every weight the family offers rather than only the one in use: it is one stylesheet either
  // way and the browser downloads only the faces it renders, so bolding the run straight after
  // picking the font gets a real bold rather than a synthesized one. No `ital` axis -- the catalog
  // ships numeric weights alone, where the server (Font#get_variants_for_weight) also asks for the
  // matching italic, so an italic run stays synthetic until a reload hands it the server's link.
  //
  // Copied before sorting: `weights` hangs off the shared window catalog, and sort is in place.
  const weights = [...(option.extraData.weights || [])].sort((a, b) => a - b)

  // Spaces become `+` and everything else is percent-encoded, matching what
  // Builder::GoogleFontLink emits; the `:`, `@` and `;` of the axis spec are css2 syntax and have
  // to stay literal.
  const encodedFamily = encodeURIComponent(family).replace(/%20/g, '+')
  const spec = weights.length
    ? `${encodedFamily}:wght@${weights.join(';')}`
    : encodedFamily

  return `https://fonts.googleapis.com/css2?family=${spec}&display=swap`
}

// Loads a font-picker option's webfont into `doc` so text just given that font renders in it right
// away. Provisional, and deliberately so: it is never authoritative and is gone by the next load,
// when Builder::HeadContentComponent puts the document's real font links in the head.
export function loadWebfont(option, doc) {
  const href = webfontStylesheetHref(option)
  if (!href || !doc?.head) return
  // Both href shapes are URL-encoded, so neither can carry a quote into the selector.
  if (doc.head.querySelector(`link[href="${href}"]`)) return

  const link = doc.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  doc.head.appendChild(link)
}
